import React, { memo, ReactElement, useCallback, useMemo, useRef, useEffect } from "react";
import {
    getCloudinaryUrl,
    getLowQImageCloudinaryUrl,
    ScreenReaderOnly,
    AriaHidden,
    getActiveElement,
    useFocusDialog,
    useFocusTrap,
} from "@swiggy-private/core-ui";
import { Image } from "@swiggy-private/core-ui";
import { TipExperimentData } from "@swiggy-private/cart-types";

import styles from "../styles/CartDeTip.scss";

interface HowItWorksProps {
    details: TipExperimentData["tipMessages"]["halfCard"];
    onChooseTipAmount: () => void;
}

const HEADER_IMAGE_DIMENSIONS = {
    height: 150,
};

const BULLET_UNICODE = "\u2022";

const HowItWorks = ({ details, onChooseTipAmount }: HowItWorksProps): ReactElement => {
    useFocusTrap();
    const headerDivRef = useRef<HTMLElement | null>(null);
    const previouslyFocusedElement = useRef<HTMLElement | null>(null);

    useEffect(() => {
        previouslyFocusedElement.current = getActiveElement();
    }, []);

    useFocusDialog(headerDivRef, previouslyFocusedElement);

    const ImageContainerStyle = {
        backgroundImage: `url(${getLowQImageCloudinaryUrl(details.imageId, HEADER_IMAGE_DIMENSIONS)})`,
    };

    const onChooseTipAmountCallback = useCallback(() => {
        if (typeof onChooseTipAmount === "function") onChooseTipAmount();
    }, [onChooseTipAmount]);

    const renderSmallMessages = useMemo(() => {
        return details.smallMessages.map((message: string) => {
            return (
                <div className={styles.hiwSmallMessagesRow} key={message}>
                    <span className={styles.hiwSmallMessagesPoint}>{BULLET_UNICODE}</span>
                    <span className={styles.hiwSmallMessagesText}>{message}</span>
                </div>
            );
        });
    }, [details.smallMessages]);

    const accessibilityText = useMemo(() => {
        let smallMessageData = "";
        details.smallMessages.forEach(message => {
            smallMessageData += " " + message;
        });

        return `${details.headMessage}${smallMessageData}`;
    }, [details]);

    return (
        <div className={styles.hiwContainer}>
            <div className={styles.hiwTopImageContainer} style={ImageContainerStyle}>
                <AriaHidden as="span">
                    <Image
                        className={styles.hiwTopImage}
                        imageUrl={getCloudinaryUrl(details.imageId, HEADER_IMAGE_DIMENSIONS)}
                    />
                </AriaHidden>
            </div>
            <ScreenReaderOnly text={accessibilityText} />
            <div className={styles.hiwTopTitle} role="heading">
                {details.title}
            </div>
            <AriaHidden as="div" className={styles.hiwMessages}>
                <span>{details.headMessage}</span>
                <div className={styles.hiwSeperator} />
                <div className={styles.hiwSmallMessagesContainer}>{renderSmallMessages}</div>
            </AriaHidden>

            <button className={styles.hiwChooseButton} onClick={onChooseTipAmountCallback}>
                {details.buttonName}
            </button>
        </div>
    );
};

export default memo(HowItWorks);
